import { useState } from "react";
import {
  Button,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { Formik } from "formik";
import * as Yup from "yup";
import api from "../../router/axiosConfig";
import { serverurl } from "../../router/config";

export default function FeedbackForm() {
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const handleSnackbarClose = () => setOpenSnackbar(false);
  const handleDialogToggle = () => setOpenDialog(!openDialog);

  const initialValues = {
    feedbacktitle: "",
    feedbackcontent: "",
  };

  const validationSchema = Yup.object({
    feedbacktitle: Yup.string().required("Title is required"),
    feedbackcontent: Yup.string().required("Details are required"),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const response = await api.post(
        `${serverurl}addFeedBack`,
        {
          title: values.feedbacktitle,
          content: values.feedbackcontent,
        },
        {
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );

      if (response.data && response.data !== -1) {
        setSnackbarType("success");
        setSnackbarMsg("Feedback submitted successfully!");
        resetForm();
      } else {
        setSnackbarType("error");
        setSnackbarMsg("Feedback submission failed.");
      }
    } catch (error) {
      setSnackbarType("error");
      setSnackbarMsg("Something went wrong.");
    } finally {
      setSubmitting(false);
      setOpenSnackbar(true);
      setOpenDialog(false);
    }
  };

  return (
    <>
      <Tooltip
        title="Post Feedback"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: "#fff",
              color: "#000",
              fontSize: 10,
              borderRadius: 1,
              boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        <IconButton color="primary" onClick={handleDialogToggle}>
          <FeedbackOutlinedIcon fontSize="large" />
        </IconButton>
      </Tooltip>

      <Dialog open={openDialog} onClose={handleDialogToggle} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            background: "linear-gradient(45deg, #0abb87, #1e4b8c)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Post Feedback
          <IconButton
            aria-label="close"
            onClick={handleDialogToggle}
            sx={{
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent dividers>
                <TextField
                  label="Title"
                  name="feedbacktitle"
                  value={values.feedbacktitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.feedbacktitle && Boolean(errors.feedbacktitle)}
                  helperText={touched.feedbacktitle && errors.feedbacktitle}
                  fullWidth
                  margin="normal"
                  multiline
                  maxRows={2}
                />

                <TextField
                  label="Details"
                  name="feedbackcontent"
                  value={values.feedbackcontent}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.feedbackcontent && Boolean(errors.feedbackcontent)}
                  helperText={touched.feedbackcontent && errors.feedbackcontent}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarType} sx={{ width: "100%" }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
